/**
 * Patient chats inbox: classify lastMessagePreview for richer list-row UI
 * (amount chips, appointment confirmed, bracketed status tags).
 */

function providerLabel(targetRole) {
  return targetRole === "hospital" ? "Hospital" : "Doctor";
}

/** INR amount as shown in previews, e.g. Rs 20,000 */
const RE_AMOUNT_ONLY = /^(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;

/**
 * Split preview amount string for typography (currency vs figures).
 * @param {string} amountStr e.g. "Rs 20,000"
 * @returns {{ currency: string | null, figure: string | null, full: string }}
 */
export function splitInrPreviewAmountDisplay(amountStr) {
  const full = normalizeSpaces(amountStr || "");
  const m = /^Rs\s+([\d,.]+(?:\.\d{1,2})?)\s*$/i.exec(full);
  if (!m) return { currency: null, figure: null, full };
  return { currency: "Rs", figure: m[1], full };
}
const RE_YOUR_BID = /^Your bid\s+(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
const RE_YOUR_COUNTER = /^Your counter\s+(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
const RE_OFFERED = /^(Doctor|Hospital)\s+offered\s+(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
const RE_ACCEPTED = /^(Doctor|Hospital)\s+accepted\s*·\s*(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
/** System timeline previews such as `Bid update · Rs 12,000` */
const RE_BID_LABEL_AMOUNT =
  /^(Bid update|Reverse bid)\s*·\s*(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
const RE_COUNTER_OFFER = /^Counter offer\s*·\s*(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;
const RE_ACCEPTED_LABEL =
  /^(Offer accepted|Provider accepted)\s*·\s*(Rs\s[\d,.]+(?:\.\d{1,2})?)\s*$/i;

function normalizeSpaces(s) {
  return String(s || "").trim().replace(/\s+/g, " ");
}

/**
 * @param {string} preview
 * @param {string} [targetRole] conversation.targetRole — doctor | hospital
 * @returns
 *   | { variant: 'text', text: string }
 *   | { variant: 'confirmed', confirmedLabel?: string }
 *   | { variant: 'amount', amount: string, caption: string }
 *   | { variant: 'status', label: string }
 */
export function parsePatientChatsInboxPreview(preview, targetRole) {
  const raw = String(preview || "").trim();
  if (!raw) return { variant: "text", text: "Tap to open" };

  const compact = normalizeSpaces(raw);

  if (
    /^\[?\s*appointment\s*confirmed\s*\]?$/i.test(compact) ||
    (/appointment\s*confirmed/i.test(compact) && compact.length <= 52)
  ) {
    return { variant: "confirmed" };
  }

  if (
    /^\[?\s*visit\s*completed\s*\]?$/i.test(compact) ||
    (/visit\s*completed/i.test(compact) && compact.length <= 52)
  ) {
    return { variant: "confirmed", confirmedLabel: "Visit completed" };
  }

  let m = compact.match(RE_AMOUNT_ONLY);
  if (m) {
    const amount = normalizeSpaces(m[1]);
    return {
      variant: "amount",
      amount,
      caption: `Quoted by ${providerLabel(targetRole)}`,
    };
  }

  m = compact.match(RE_YOUR_BID);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[1]),
      caption: "You proposed this amount",
    };
  }

  m = compact.match(RE_YOUR_COUNTER);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[1]),
      caption: "Your counter offer",
    };
  }

  m = compact.match(RE_OFFERED);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[2]),
      caption: `Offer from ${m[1]}`,
    };
  }

  m = compact.match(RE_ACCEPTED);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[2]),
      caption: `Agreed · ${m[1]}`,
    };
  }

  m = compact.match(RE_BID_LABEL_AMOUNT);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[2]),
      caption: `Quoted by ${providerLabel(targetRole)}`,
    };
  }

  m = compact.match(RE_COUNTER_OFFER);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[1]),
      caption: "Your counter offer",
    };
  }

  m = compact.match(RE_ACCEPTED_LABEL);
  if (m) {
    return {
      variant: "amount",
      amount: normalizeSpaces(m[2]),
      caption: `Agreed · ${providerLabel(targetRole)}`,
    };
  }

  const bracket = /^\[([^\]]+)\]\s*$/.exec(compact);
  if (bracket) {
    let label = bracket[1].trim().replace(/!+$/g, "").trim();
    if (/appointment\s*confirmed/i.test(label)) {
      return { variant: "confirmed" };
    }
    if (/visit\s*completed/i.test(label)) {
      return { variant: "confirmed", confirmedLabel: "Visit completed" };
    }
    return { variant: "status", label };
  }

  return { variant: "text", text: raw };
}
